import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as maptilersdk from '@maptiler/sdk';
import { createRoot } from 'react-dom/client';
import '@maptiler/sdk/dist/maptiler-sdk.css';
import './map.css';
import { Vendor, MapBounds, MapProps, LocationGroup } from '../../types/types';
import { logger } from '../../lib/logger';
import { format } from 'date-fns';
import {
  addPulseStyles,
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  getLatestEvent,
  getMarkerStyles,
  groupVendorsByLocation,
  hasBoundsChangedSignificantly,
  MAX_ZOOM,
  parseTimeString,
  shouldShowMarkersAtZoom,
  VIEWPORT_CHANGE_DELAY,
} from '../../lib/mapUtils';
import EventCarousel from './Popups/EventCarousel';
import GCalEventCard from './Popups/GCalEventCard';
import { HIDE_DELAY_MS, MAP_EASE_TO_DURATION } from '../../lib/appConstants';
import {
  addTouchDebugStyles,
  createEnhancedMarkerElement,
  createEnhancedMarkerHandler,
} from '../../lib/enhancedMarkerTouch';
import { popupManager } from '../../lib/popupManager';
import { markerManager } from '../../lib/markerManager';
import { createLongPressHandlers } from '../../lib/longPressUtils';
import { LongPressIndicator } from './LongPressIndicator';
import { usePreviewMarkerOverlay } from '../../lib/previewMarkerUtils';
import { PreviewPin } from './PreviewPin';

interface EnhancedMarker extends maptilersdk.Marker {
  _lastClickTime?: number;
  _popupShouldExpand?: boolean;
  _ceenLocationKey?: string;
  _ceenVendorIds?: number[];
}

const MapComponent = ({
  filteredVendors,
  selectedVendorId,
  selectedDisplayIds,
  onMarkerClick,
  onViewportChange,
  onVisibleVendorsChange,
  isMobile = false,
  currentDate,
  locationState,
  onLocationPermissionChange,
  onMapTouched,
  onLocatingChange,
  onSignUpModalRequest,
  onShareEvent,
  showToast,
  onMapRef,
  onZoomChange,
  shareViewState,
  activeVendorIds,
  selectedCity,
  activeListItems,
  onPinEvent,
  onUnpinEvent,
  onMapLongPress,
  onEditEvent,
  previewMarkerCoords,
  onPreviewMarkerDrag,
  previewMarkerPopping,
  gcalEvents,
}: MapProps) => {
  logger.debug('🔄 MapComponent rendering - ALL props:');
  logger.debug('  - filteredVendors:', filteredVendors.length);
  logger.debug('  - selectedVendorId:', selectedVendorId);

  useEffect(() => {
    (window as any).handleVendorSelect = (vendorId: number, displayId?: string) => {
      // Call selection handler immediately for instant UI update
      if (onMarkerClick) {
        onMarkerClick(vendorId, displayId);
      } else {
        logger.debug(`❌ onMarkerClick is null/undefined in handleVendorSelect`);
      }
    };

    (window as any).handleEventZoom = (coordinates: [number, number], _eventCount: number) => {
      // Close all popups before zooming
      markerManager.getAllMarkers().forEach(m => {
        const popup = m.getPopup();
        if (popup && popup.isOpen()) {
          popup.remove();
        }
      });

      if (map.current) {
        const currentZoom = map.current.getZoom();
        const targetZoom = Math.min(currentZoom + 2, 18);
        logger.debug(`🔍 Zooming from ${currentZoom} to ${targetZoom}`);

        map.current.easeTo({
          center: coordinates,
          zoom: targetZoom,
          duration: MAP_EASE_TO_DURATION,
          padding: { top: 50, bottom: 50, left: 50, right: 50 },
        });
      }
    };

    // handler for panel-triggered interactions
    (window as any).handlePanelCardClick = (
      vendorId: number,
      displayId: string,
      coordinates: [number, number],
      shouldBeSelected = true
    ) => {
      if (!map.current) return;

      isProgrammaticMove.current = true;

      // NEW: Check if we're zoomed out below marker threshold
      const currentZoom = map.current.getZoom();
      const needsZoomIn = !shouldShowMarkersAtZoom(currentZoom);

      if (needsZoomIn) {
        // Zoom in first, then handle marker interaction after markers are created
        map.current.easeTo({
          center: coordinates,
          zoom: DEFAULT_ZOOM,
          duration: MAP_EASE_TO_DURATION,
          padding: { top: 100, bottom: 100, left: 50, right: 50 },
        });

        map.current.once('moveend', () => {
          // Give markers time to render AND map to stabilize
          setTimeout(() => {
            isProgrammaticMove.current = false;

            // Safety check: ensure map still exists
            if (!map.current) {
              logger.warn('⚠️ Map no longer exists after zoom');
              return;
            }

            const targetMarker = markerManager.getAllMarkers().find(marker => {
              const markerCoords = marker.getLngLat();
              return (
                Math.abs(markerCoords.lng - coordinates[0]) < 0.000001 &&
                Math.abs(markerCoords.lat - coordinates[1]) < 0.000001
              );
            });

            if (targetMarker) {
              // Set expand flag AND intended displayId
              (targetMarker as any)._popupShouldExpand = shouldBeSelected;
              (targetMarker as any)._intendedDisplayId = displayId;

              // SAFER: Check popup and map state before toggling
              const popup = targetMarker.getPopup();
              if (popup && !popup.isOpen() && map.current) {
                try {
                  // CRITICAL: Close other popups RIGHT BEFORE opening new one
                  popupManager.closeAllExcept(targetMarker, markerManager.getAllMarkers());

                  (popup as any)._intendedSelection = shouldBeSelected;
                  (popup as any)._intendedDisplayId = displayId;

                  // Use addTo instead of togglePopup for more control
                  popup.setLngLat(targetMarker.getLngLat());
                  popup.addTo(map.current);

                  // Register popup with PopupManager
                  popupManager.register(popup, vendorId);
                } catch (error) {
                  logger.error('Error opening popup:', error);
                  // Fallback: just select without popup
                  if (onMarkerClick) {
                    onMarkerClick(vendorId, displayId, true);
                  }
                }
              }
            } else {
              logger.warn('⚠️ Could not find marker even after zoom in');
              // Fallback: just select the vendor without popup
              if (onMarkerClick) {
                onMarkerClick(vendorId, displayId, true);
              }
            }
          }, 500); // Increased timeout to ensure map is stable
        });

        return;
      }

      // EXISTING CODE: Already zoomed in enough, proceed normally
      const targetMarker = markerManager.getAllMarkers().find(marker => {
        const markerCoords = marker.getLngLat();
        return (
          Math.abs(markerCoords.lng - coordinates[0]) < 0.000001 &&
          Math.abs(markerCoords.lat - coordinates[1]) < 0.000001
        );
      });

      if (targetMarker) {
        // Set expand flag AND intended displayId
        (targetMarker as any)._popupShouldExpand = shouldBeSelected;
        (targetMarker as any)._intendedDisplayId = displayId;

        map.current.easeTo({
          center: coordinates,
          zoom: 14,
          duration: MAP_EASE_TO_DURATION,
          padding: { top: 100, bottom: 100, left: 50, right: 50 },
        });

        map.current.once('moveend', () => {
          // Wait for animation to fully complete
          setTimeout(() => {
            isProgrammaticMove.current = false;

            // Safety check
            if (!map.current) return;

            const popup = targetMarker.getPopup();
            logger.log('Attempting to open popup:', {
              popupExists: !!popup,
              isOpen: popup?.isOpen(),
              isMobile: isMobile,
            });

            if (popup && !popup.isOpen()) {
              try {
                // CRITICAL: Close other popups RIGHT BEFORE opening new one
                // Do this here, not earlier, because previous popup may have been added async
                popupManager.closeAllExcept(targetMarker, markerManager.getAllMarkers());

                (popup as any)._intendedSelection = shouldBeSelected;
                (popup as any)._intendedDisplayId = displayId;

                // Use addTo instead of togglePopup
                popup.setLngLat(targetMarker.getLngLat());
                popup.addTo(map.current!);

                // Register popup with PopupManager
                popupManager.register(popup, vendorId);

                logger.log('Popup opened successfully (tracked)');
              } catch (error) {
                logger.log('Error opening popup, using fallback:', error);
                // Fallback
                if (onMarkerClick) {
                  onMarkerClick(vendorId, displayId, true);
                }
              }
            }
          }, 100);
        });
      } else {
        // Marker not found
        logger.warn('⚠️ Could not find marker for panel click');
        isProgrammaticMove.current = false;
        if (onMarkerClick) {
          onMarkerClick(vendorId, displayId, true);
        }
      }
    };

    return () => {
      delete (window as any).handleVendorSelect;
      delete (window as any).handleEventZoom;
      delete (window as any).handlePanelCardClick;
    };
  }, [onMarkerClick]);

  logger.debug(
    'Map received vendors:',
    filteredVendors.length,
    filteredVendors.map(v => v.id)
  );

  // State to store user's location
  const [userLocation, setUserLocation] = useState<{ lng: number; lat: number } | null>(null);
  const [_locationError, setLocationError] = useState<string | null>(null);
  const [_geolocateState, setGeolocateState] = useState<'idle' | 'locating' | 'granted' | 'denied'>(
    'idle'
  );
  const [mapTouchStarted, setMapTouchStarted] = useState<boolean>(false);
  const [lpIndicator, setLpIndicator] = useState<{
    active: boolean;
    x: number;
    y: number;
    fired: boolean;
  } | null>(null);
  const [toastMessage, setToastMessage] = useState<{
    type: 'error' | 'success';
    text: string;
    timestamp: number;
    isDismissing?: boolean;
  } | null>(null);

  // Refs
  const map = useRef<maptilersdk.Map | null>(null);
  const mapContainer = useRef<HTMLDivElement>(null);
  // MarkerManager handles marker storage internally
  // We just need a ref to trigger re-renders when needed
  const geolocateControl = useRef<maptilersdk.GeolocateControl | null>(null);
  // openPopupsOnMap and openPopupMarkerId refs removed - now handled by PopupManager
  const viewportChangeTimer = useRef<NodeJS.Timeout | null>(null);
  const initialViewportUpdateRef = useRef<boolean>(false);
  const touchEnabled = useRef<boolean>(false);
  const popupRenderFunctions = useRef<Map<string, (selectedDisplayIds: string[]) => void>>(
    new Map()
  );
  const activeListItemsRef = useRef(activeListItems);
  const locationGroupsRef = useRef<LocationGroup[]>([]);
  const lastMapBoundsRef = useRef<MapBounds | null>(null);
  const lastVendorsHashRef = useRef<string>('');
  const isProgrammaticMove = useRef(false);
  const userLocationMarker = useRef<maptilersdk.Marker | null>(null);
  const hasFlownToPreview = useRef(false);
  const previewFlyToActive = useRef(false);
  const prevVendorIdsRef = useRef<Set<string>>(new Set());

  // Handle zoom changes and notify parent
  const handleZoomChange = useCallback(() => {
    if (!map.current || !onZoomChange) return;

    const currentZoom = map.current.getZoom();
    onZoomChange(currentZoom);
  }, [onZoomChange]);

  // Helper to determine if a vendor is a "free to show up" event
  const isFreeEvent = useCallback((vendor: Vendor): boolean => {
    const requirements = vendor.metadata?.requirements;
    return (
      Array.isArray(requirements) && requirements.length === 1 && requirements[0] === 'just show up'
    );
  }, []);

  // Add pulse animation styles on component mount
  useEffect(() => {
    addPulseStyles();
    addTouchDebugStyles();
  }, []);

  // Fly to city-block zoom when event placement begins (once per session).
  // The preview pin visual is rendered via portal (see return below) so it
  // appears above the modal backdrop regardless of the map container's z-index.
  useEffect(() => {
    if (!map.current || !previewMarkerCoords) {
      hasFlownToPreview.current = false;
      previewFlyToActive.current = false;
      return;
    }
    if (hasFlownToPreview.current) return;
    hasFlownToPreview.current = true;
    previewFlyToActive.current = true;
    map.current.flyTo({
      center: previewMarkerCoords,
      zoom: 16,
      duration: 1200,
      // Bottom padding keeps the pin visible above the bottom sheet
      padding: { top: 60, bottom: 280, left: 60, right: 60 },
    });
    map.current.once('moveend', () => {
      previewFlyToActive.current = false;
    });
  }, [previewMarkerCoords]);

  // Screen position + drag for the portal pin overlay
  const {
    screenPos: previewScreenPos,
    handlePointerDown: handlePreviewPointerDown,
    isDragging: isPreviewDragging,
  } = usePreviewMarkerOverlay(map, previewMarkerCoords ?? null, onPreviewMarkerDrag);

  // Automatically dismiss toast messages after 3.5 seconds with animation
  // TODO: might have issues with the duck duck go broswers on the phone
  useEffect(() => {
    if (!toastMessage) return;

    const dismissTime = toastMessage.timestamp + HIDE_DELAY_MS; // 3.5 seconds from when shown
    const timeUntilDismiss = dismissTime - Date.now();

    // If already past dismiss time, dismiss immediately
    if (timeUntilDismiss <= 0) {
      setToastMessage(null);
      return;
    }

    // Set timeout to start dismissing animation
    const dismissTimeoutId = setTimeout(() => {
      setToastMessage(current => {
        if (current?.timestamp === toastMessage.timestamp) {
          return { ...current, isDismissing: true };
        }
        return current;
      });
    }, timeUntilDismiss);

    // Set timeout to actually remove after animation completes (300ms animation duration)
    const removeTimeoutId = setTimeout(() => {
      setToastMessage(current => {
        if (current?.timestamp === toastMessage.timestamp) {
          return null;
        }
        return current;
      });
    }, timeUntilDismiss + 300); // 300ms matches our animation duration

    return () => {
      clearTimeout(dismissTimeoutId);
      clearTimeout(removeTimeoutId);
    };
  }, [toastMessage]);

  const getEventForCurrentDate = useCallback(
    (vendors: Vendor[], currentDate: Date): Vendor | null => {
      if (!currentDate) {
        return getLatestEvent(vendors); // fallback to existing logic
      }

      // Filter vendors that should appear on this date based on their schedule
      const validVendors = vendors.filter(vendor => {
        const jsDay = currentDate.getDay();
        const dbDay = jsDay === 0 ? 7 : jsDay;
        return vendor.schedule.days.includes(dbDay);
      });

      if (validVendors.length === 0) {
        return getLatestEvent(vendors); // fallback
      }

      // Sort by start time and return the first one
      return validVendors.sort((a, b) => {
        const aTime = parseTimeString(a.localized_schedule?.start_time || a.schedule.start_time);
        const bTime = parseTimeString(b.localized_schedule?.start_time || b.schedule.start_time);
        return aTime - bTime;
      })[0];
    },
    []
  );

  // Handle viewport changes (memoized to avoid recreating on every render)
  const handleViewportChange = useCallback(() => {
    // Skip API calls during programmatic movement or while preview marker flyTo is active
    if (isProgrammaticMove.current || previewFlyToActive.current) {
      return;
    }

    if (!map.current || !onViewportChange) return;

    // Clear any existing timer
    if (viewportChangeTimer.current) {
      clearTimeout(viewportChangeTimer.current);
      logger.debug(`  - Cleared existing timer`);
    }

    // Set a new timer to delay the viewport change event
    viewportChangeTimer.current = setTimeout(() => {
      if (!map.current) return;

      // Get the current viewport bounds
      const bounds = map.current.getBounds();
      const mapBounds: MapBounds = {
        minLng: bounds.getWest(),
        minLat: bounds.getSouth(),
        maxLng: bounds.getEast(),
        maxLat: bounds.getNorth(),
      };

      if (
        lastMapBoundsRef.current &&
        !hasBoundsChangedSignificantly(lastMapBoundsRef.current, mapBounds)
      ) {
        logger.debug('Bounds change not significant, skipping parent update');
        return;
      }

      lastMapBoundsRef.current = mapBounds;
      // Call the onViewportChange callback with the bounds
      onViewportChange(mapBounds);

      // Calculate which vendors are visible in the current viewport
      if (onVisibleVendorsChange) {
        const visibleVendorIds = filteredVendors
          .filter(vendor => {
            // Skip vendors without coordinates
            if (!vendor.coordinates || vendor.coordinates.length !== 2) {
              logger.debug(`Vendor ${vendor.id} has invalid coordinates:`, vendor.coordinates);
              return false;
            }

            // Extract coordinates - vendor.coordinates is [longitude, latitude]
            const lng = vendor.coordinates[0];
            const lat = vendor.coordinates[1];

            // Check if vendor coordinates are within the current viewport bounds
            const isVisible =
              lng >= mapBounds.minLng &&
              lng <= mapBounds.maxLng &&
              lat >= mapBounds.minLat &&
              lat <= mapBounds.maxLat;

            // Detailed logging for each vendor's visibility check
            logger.debug(
              `Vendor ${vendor.id} (${vendor.name}) at [${lng},${lat}], uses_gps: ${vendor.uses_gps}, visible: ${isVisible}`
            );

            return isVisible;
          })
          .map(vendor => vendor.id);

        logger.debug('Visible vendor IDs:', visibleVendorIds);

        // Report back to parent component
        onVisibleVendorsChange(visibleVendorIds);
      }
    }, VIEWPORT_CHANGE_DELAY);
  }, [onViewportChange, onVisibleVendorsChange, filteredVendors]);

  const createUserLocationMarkerElement = (): HTMLElement => {
    const container = document.createElement('div');
    container.className = 'custom-user-location';
    container.style.cssText = `
      position: relative;
      width: 20px;
      height: 20px;
    `;

    // Pulsing ring
    const pulseRing = document.createElement('div');
    pulseRing.className = 'custom-user-pulse';
    pulseRing.style.cssText = `
      position: absolute;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: var(--color-location-ghost);
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      animation: userPulse 2s infinite ease-out;
    `;

    // Location dot
    const dot = document.createElement('div');
    dot.className = 'custom-user-dot';
    dot.style.cssText = `
      position: absolute;
      width: 16px;
      height: 16px;
      background-color: var(--color-location);
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.2);
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 10;
    `;

    container.appendChild(pulseRing);
    container.appendChild(dot);

    return container;
  };

  // Handle user touch events to prevent body scrolling when map is interacted with
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (mapContainer.current && mapContainer.current.contains(e.target as Node)) {
      setMapTouchStarted(true);
      // Prevent default only for map container touches to avoid breaking other UI interactions
      e.preventDefault();
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (mapTouchStarted) {
        // Prevent default to stop page scrolling/zooming when interacting with map
        e.preventDefault();
      }
    },
    [mapTouchStarted]
  );

  const handleTouchEnd = useCallback(() => {
    setMapTouchStarted(false);
  }, []);

  // Add proper touch event handling for the entire document
  useEffect(() => {
    // Check if touch is available
    const isTouchDevice =
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      (navigator as any).msMaxTouchPoints > 0;

    touchEnabled.current = isTouchDevice;

    if (isTouchDevice) {
      logger.debug('Touch device detected, enabling enhanced touch support');

      // Add document-level touch handlers
      document.addEventListener('touchstart', handleTouchStart, { passive: false });
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);

      // Fix for iOS overscroll behavior - use CSS properties that work
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'relative';

      // Apply specific iOS fixes if needed
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100%';
      }
    }

    return () => {
      if (isTouchDevice) {
        document.removeEventListener('touchstart', handleTouchStart);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);

        // Reset body styles
        document.body.style.overflow = '';
        document.body.style.position = '';

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOS) {
          document.body.style.position = '';
          document.body.style.width = '';
          document.body.style.height = '';
        }
      }
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Map initialization
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    const apiKey = process.env.REACT_APP_MAPTILER_KEY;
    if (!apiKey) {
      logger.error('Maptiler API key not found in environment variables');
      return;
    }

    // Important: Check if site is served over HTTPS
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      logger.warn('Geolocation may not work because the site is not served over HTTPS');
      setLocationError('Geolocation requires HTTPS connection');
    }

    maptilersdk.config.apiKey = apiKey;

    // Helper to trigger viewport change if needed
    const triggerDefaultViewport = () => {
      if (onViewportChange && map.current) {
        const bounds = map.current.getBounds();
        const mapBounds: MapBounds = {
          minLng: bounds.getWest(),
          minLat: bounds.getSouth(),
          maxLng: bounds.getEast(),
          maxLat: bounds.getNorth(),
        };
        onViewportChange(mapBounds);
      }
    };

    // Determine map center: share view > user location > default
    let center: { lng: number; lat: number };
    let initialZoom = DEFAULT_ZOOM;

    if (shareViewState?.active && shareViewState.coordinates) {
      // Share link takes priority - fly to shared event
      center = {
        lng: shareViewState.coordinates[0],
        lat: shareViewState.coordinates[1],
      };
      logger.debug('Map initializing with share view coordinates:', center);
    } else if (userLocation) {
      // User location
      center = userLocation;
    } else if (selectedCity) {
      center = { lng: selectedCity.lng, lat: selectedCity.lat };
      initialZoom = selectedCity.zoom;
    } else {
      // Default fallback
      center = DEFAULT_CENTER;
      logger.debug('Map initializing with default center');
    }

    // Configure map options with better touch support
    const mapOptions: maptilersdk.MapOptions = {
      container: mapContainer.current,
      style: 'https://api.maptiler.com/maps/0196f056-ed44-74c9-853c-431a690732aa/style.json',
      center: [center.lng, center.lat],
      zoom: initialZoom,
      maxZoom: MAX_ZOOM,
      navigationControl: false,
      geolocateControl: false,
      interactive: true,
      touchZoomRotate: true,
      dragPan: true,
      touchPitch: true,
      trackResize: true,
      cooperativeGestures: false, // Disable cooperative gestures for better mobile experience
    };

    map.current = new maptilersdk.Map(mapOptions);

    // Add geolocate control for both mobile and desktop
    geolocateControl.current = new maptilersdk.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: false,
      showAccuracyCircle: false, // ceen custom used
      showUserLocation: false, // Disable SDK's marker
    });

    // Add custom class for positioning
    geolocateControl.current._container?.classList.add('maptiler-geolocate-control');

    // Add to map
    map.current.addControl(geolocateControl.current);

    if (!navigator.geolocation) {
      onLocatingChange?.(false);
      if (onLocationPermissionChange) {
        onLocationPermissionChange('unavailable', null);
      }
      triggerDefaultViewport();
    }

    // Setup event handlers for the geolocate control
    geolocateControl.current.on('geolocate', position => {
      const location = {
        lng: position.coords.longitude,
        lat: position.coords.latitude,
      };

      setUserLocation(location);
      setLocationError(null);
      onLocatingChange?.(false);
      setGeolocateState('granted');
      window.dispatchEvent(
        new CustomEvent('geolocateStateChange', { detail: { state: 'granted' } })
      );

      if (onLocationPermissionChange) {
        onLocationPermissionChange('granted', location);
      }

      if (map.current) {
        // Create or update user location marker
        if (!userLocationMarker.current) {
          // Create marker for the first time
          const markerElement = createUserLocationMarkerElement();

          userLocationMarker.current = new maptilersdk.Marker({
            element: markerElement,
            anchor: 'center',
          })
            .setLngLat([location.lng, location.lat])
            .addTo(map.current);

          logger.debug('Created custom user location marker');
        } else {
          // Update existing marker position
          userLocationMarker.current.setLngLat([location.lng, location.lat]);
          logger.debug('Updated custom user location marker position');
        }

        // Always fly to user location at default zoom
        map.current.flyTo({
          center: [location.lng, location.lat],
          zoom: DEFAULT_ZOOM,
          speed: 1.2,
          curve: 1.42,
          padding: { top: 50, bottom: 50, left: 50, right: 50 },
        });
      }
    });

    geolocateControl.current.on('error', error => {
      logger.error('Geolocation error:', error);
      setLocationError(`Couldn't get your location: ${error.message}`);
      onLocatingChange?.(false);
      setGeolocateState('denied');
      window.dispatchEvent(
        new CustomEvent('geolocateStateChange', { detail: { state: 'denied' } })
      );
      if (onLocationPermissionChange) {
        onLocationPermissionChange('denied', null);
      }
      triggerDefaultViewport();
    });

    // Listen for manual geolocate button tap
    const handleTriggerGeolocate = () => {
      if (geolocateControl.current) {
        geolocateControl.current.trigger();
        setGeolocateState('locating');
        window.dispatchEvent(
          new CustomEvent('geolocateStateChange', { detail: { state: 'locating' } })
        );
        onLocatingChange?.(true);
      }
    };
    window.addEventListener('triggerGeolocation', handleTriggerGeolocate);

    // Apply map container styles to make touch work better
    if (mapContainer.current) {
      // Set critical touch-related CSS properties directly
      mapContainer.current.style.touchAction = 'none';
      (mapContainer.current.style as any).WebkitOverflowScrolling = 'touch';

      // Prevent scrolling when touching the map
      mapContainer.current.addEventListener(
        'touchstart',
        _e => {
          // Don't prevent default here as we do it at document level conditionally
          logger.debug('Touch start on map container');
        },
        { passive: true }
      );
    }

    // Add map click handler to close popups when clicking elsewhere
    map.current.on('click', _e => {
      // Close all popups when clicking on empty map area
      markerManager.getAllMarkers().forEach(m => {
        const popup = m.getPopup();
        if (popup && popup.isOpen()) {
          popup.remove();
        }
      });
    });

    // Position controls after map loads
    map.current.once('load', () => {
      // Try to trigger geolocate after a short delay
      // BUT skip if we're in share view mode - we want to stay at shared location
      setTimeout(() => {
        const skipGeolocation = shareViewState?.active || !!selectedCity;
        if (geolocateControl.current && !skipGeolocation) {
          geolocateControl.current.trigger();
          onLocatingChange?.(true);
        } else if (shareViewState?.active) {
          logger.log('Skipping auto-geolocation - in share view mode');
          handleViewportChange();
        } else {
          // City preset path — just trigger viewport change for initial bounds
          handleViewportChange();
        }

        if (geolocateControl.current && geolocateControl.current._container) {
          const geolocateEl = geolocateControl.current._container;
          geolocateEl.style.zIndex = '10';
        }
      }, 1000);
    });

    return () => {
      window.removeEventListener('triggerGeolocation', handleTriggerGeolocate);
      if (userLocationMarker.current) {
        userLocationMarker.current.remove();
        userLocationMarker.current = null;
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [isMobile, handleTouchStart]);

  useEffect(() => {
    if (onMapRef && map.current) {
      onMapRef(map.current);
    }
  }, [onMapRef]);
  // Add event listeners for map events
  useEffect(() => {
    if (!map.current || !onViewportChange) return;

    logger.debug('Setting up map event listeners');

    // Remove any existing listeners first to prevent duplicates
    map.current.off('moveend', handleViewportChange);
    map.current.off('zoomend', handleViewportChange);

    // Add event listeners with current handleViewportChange function
    map.current.on('moveend', handleViewportChange);
    map.current.on('zoomend', handleViewportChange);
    map.current.on('zoom', handleZoomChange);

    // Trigger initial viewport update if map is loaded
    if (map.current.loaded() && !initialViewportUpdateRef.current) {
      logger.debug('Map is loaded, triggering initial viewport update');
      initialViewportUpdateRef.current = true;
      handleViewportChange();
      handleZoomChange(); // Also send initial zoom
    } else {
      logger.debug('Map is not yet loaded, waiting for load event');
      map.current.once('load', () => {
        logger.debug('Map loaded, triggering initial viewport update');
        handleViewportChange();
        handleZoomChange();
      });
    }

    // Enable direct gesture support on the map - critical for touch devices
    if (touchEnabled.current && map.current) {
      map.current.dragRotate.disable(); // Usually better UX to disable rotate on mobile
      map.current.touchZoomRotate.disableRotation();

      // Make sure drag pan and touch zoom are enabled
      map.current.dragPan.enable();
      map.current.touchZoomRotate.enable();
    }

    return () => {
      if (map.current) {
        map.current.off('moveend', handleViewportChange);
        map.current.off('zoomend', handleViewportChange);
        map.current.off('zoom', handleZoomChange);
      }

      if (viewportChangeTimer.current) {
        clearTimeout(viewportChangeTimer.current);
      }
    };
  }, []);

  const setupMarkers = () => {
    const mapLoaded = map.current!.loaded();

    const isDisambiguating = (map.current as any)._ceenDisambiguationZoom;
    logger.log('🔍 setupMarkers trigger analysis:', {
      mapLoaded,
      isDisambiguating,
      filteredVendorsCount: filteredVendors.length,
      selectedVendorId,
      currentZoom: map.current!.getZoom(),
    });

    const currentZoom = map.current!.getZoom();

    // Skip marker setup during disambiguation zoom
    if (isDisambiguating) {
      logger.log('🚫 Skipping marker setup during disambiguation zoom');
      return;
    }

    // if (!map.current?.loaded()) {
    //   logger.debug('⏳ Map not loaded yet, skipping marker setup');
    //   return;
    // }

    if (locationState === 'pending') {
      logger.debug('Location permission pending, skipping marker setup');
      return;
    }

    // OPTIMIZATION: Only recalculate location groups if vendors actually changed
    const vendorsHash = filteredVendors
      .map(v => `${v.id}-${v.coordinates?.[0]}-${v.coordinates?.[1]}`)
      .sort()
      .join('|');

    let locationGroups: LocationGroup[];
    if (vendorsHash === lastVendorsHashRef.current && locationGroupsRef.current.length > 0) {
      // Use cached location groups
      locationGroups = locationGroupsRef.current;
      logger.debug('📊 Using cached location groups');
    } else {
      // Recalculate and cache
      locationGroups = groupVendorsByLocation(filteredVendors);
      locationGroupsRef.current = locationGroups;
      lastVendorsHashRef.current = vendorsHash;
      logger.debug('📊 Recalculated location groups');
    }

    // Clean up vendor markers only — GCal markers survive vendor recreation
    const vendorMarkers = markerManager.getAllMarkersByType('vendor');
    popupManager.cleanupBeforeMarkerRecreation(vendorMarkers);
    markerManager.cleanupByType('vendor');
    popupRenderFunctions.current.clear();

    const nextVendorIds = new Set<string>();

    locationGroups.forEach(group => {
      // Use the ref to get the LATEST selection state
      const representativeVendor = currentDate
        ? getEventForCurrentDate(group.vendors, currentDate) || getLatestEvent(group.vendors)
        : getLatestEvent(group.vendors);
      const representativeIsFree = isFreeEvent(representativeVendor);

      const markerElement = createEnhancedMarkerElement(
        representativeVendor,
        currentZoom,
        group.vendors.length,
        getMarkerStyles,
        representativeIsFree
      );

      // Apply private event marker styling for own events that aren't public
      if (
        representativeVendor.is_own_event === true &&
        representativeVendor.visibility !== 'public'
      ) {
        markerElement.classList.add('marker-private');
      }

      // Create popup container and React root
      const popupContainer = document.createElement('div');
      const root = createRoot(popupContainer);

      const locationKey = `${group.coordinates[0]},${group.coordinates[1]}`;

      // Create marker with MarkerManager (stores root for cleanup)
      const marker = markerManager.createMarker(
        markerElement,
        [group.coordinates[0], group.coordinates[1]],
        root,
        locationKey,
        group.vendors.map(v => v.id)
      );

      // Add marker to map
      markerManager.addToMap(locationKey, map.current!);

      marker.on('drag', () => {
        logger.log('🔄 Marker drag detected:', group.vendors[0].id);
      });

      // Track the popup's initial expansion state
      (marker as any)._popupShouldExpand = false;
      // Store marker with location info for easy lookup
      (marker as any)._ceenLocationKey = `${group.coordinates[0]},${group.coordinates[1]}`;
      (marker as any)._ceenVendorIds = group.vendors.map(v => v.id);

      const today = currentDate || new Date();
      const displayDate = format(today, 'yyyy-MM-dd');
      const vendorDisplayId = `${representativeVendor.id}-${displayDate}`;
      nextVendorIds.add(vendorDisplayId);
      if (!prevVendorIdsRef.current.has(vendorDisplayId)) {
        markerElement.classList.add('vendor-popin');
      }
      // Create the popup content render function
      const renderPopupContent = (currentSelectedDisplayIds: string[]) => {
        const shouldExpand = (marker as any)._popupShouldExpand || false;

        root.render(
          <EventCarousel
            vendors={group.vendors}
            currentDate={today}
            selectedDisplayIds={currentSelectedDisplayIds} // Use the passed parameter
            onEventSelect={(vendorId, displayId) => {
              if (onMarkerClick) {
                onMarkerClick(vendorId, displayId, true);
              }
            }}
            isPopup={true}
            initiallyExpanded={shouldExpand}
            isMobile={isMobile}
            onSignUpModalRequest={onSignUpModalRequest}
            onShareEvent={onShareEvent}
            showToast={showToast}
            activeListItems={activeListItemsRef.current}
            onPinEvent={onPinEvent}
            onUnpinEvent={onUnpinEvent}
            onEditEvent={onEditEvent}
          />
        );
      };

      // Store the render function and update cached groups
      popupRenderFunctions.current.set(locationKey, renderPopupContent);

      // Get the current marker size for proper offset calculation
      const markerStyles = getMarkerStyles(false, representativeIsFree);
      const markerSize = parseInt(markerStyles.width.replace('px', ''));
      const dynamicOffset = markerSize / 2 + 5; // Half marker size plus small buffer

      const popup = new maptilersdk.Popup({
        closeButton: false,
        closeOnClick: false,
        closeOnMove: false,
        focusAfterOpen: false,
        maxWidth: isMobile ? '280px' : '320px',
        className: 'clean-popup',
        offset: dynamicOffset,
      }).setDOMContent(popupContainer);

      marker.setPopup(popup);

      popup.on('open', () => {
        // Register popup with PopupManager (also sets the marker ID internally)
        popupManager.register(popup, representativeVendor.id);

        const visualMarker = markerElement.querySelector('.marker-visual') as HTMLElement;
        if (visualMarker) {
          const styles = getMarkerStyles(true, representativeIsFree);
          Object.assign(visualMarker.style, styles);
        }

        const intendedDisplayId = (popup as any)._intendedDisplayId;
        const displayIdsToUse = intendedDisplayId ? [intendedDisplayId] : selectedDisplayIds || [];

        renderPopupContent(displayIdsToUse);

        (popup as any)._intendedDisplayId = null;
        delete (popup as any)._intendedSelection;
        delete (popup as any)._intendedDisplayId;
      });

      popup.on('close', () => {
        // Track popup state
        popupManager.unregister(popup);

        if (popupManager.getOpenMarkerId() === representativeVendor.id) {
          // Marker ID now managed by PopupManager
          // Update this marker's style back to green
          const visualMarker = markerElement.querySelector('.marker-visual') as HTMLElement;
          if (visualMarker) {
            const styles = getMarkerStyles(false, representativeIsFree);
            Object.assign(visualMarker.style, styles);
          }
          // Reset expand flag
          (marker as any)._popupShouldExpand = false;
        }
      });

      // Make popup content respond to touch for expansion
      if (touchEnabled.current) {
        popupContainer.style.cursor = 'pointer';
        popupContainer.style.userSelect = 'none';
        popupContainer.style.touchAction = 'manipulation';
      }

      const enhancedHandler = createEnhancedMarkerHandler(
        marker as EnhancedMarker,
        group,
        popup,
        onMarkerClick,
        markerManager, // Pass markers ref
        map, // Pass map ref
        isProgrammaticMove // Pass isProgrammaticMove ref
      );

      // Add to touch area (the larger clickable area)
      const touchArea = markerElement.querySelector('.marker-touch-area') as HTMLElement;
      if (touchArea) {
        touchArea.addEventListener('click', enhancedHandler);
        markerManager.registerEventListener(locationKey, touchArea, 'click', enhancedHandler);

        if (touchEnabled.current) {
          const options: AddEventListenerOptions = { passive: true };
          touchArea.addEventListener('touchend', enhancedHandler, options);
          markerManager.registerEventListener(
            locationKey,
            touchArea,
            'touchend',
            enhancedHandler,
            options
          );
        }
      }
    });
    const debugInfo = markerManager.getDebugInfo();
    logger.debug('MarkerManager state:', debugInfo);

    prevVendorIdsRef.current = nextVendorIds;

    // Update the reference used by selection updates
    locationGroupsRef.current = locationGroups;

    // Reapply active dimming state after marker recreation
    const currentActiveVendorIds = activeVendorIds ?? null;
    if (currentActiveVendorIds !== null) {
      markerManager.applyDimming(currentActiveVendorIds);
    }
  };

  // MARKER SETUP useEffect - sets up markers when vendors or location state changes
  useEffect(() => {
    if (!map.current) return;

    const setupMarkersWhenReady = () => {
      // CRITICAL: Skip during programmatic moves (panel clicks)
      // This prevents marker recreation which orphans popup references
      if (isProgrammaticMove.current) {
        return;
      }

      // Prevent recursive calls
      if ((map.current as any)._settingUpMarkers) {
        return;
      }

      (map.current as any)._settingUpMarkers = true;

      try {
        // Disambiguation check
        if ((map.current as any)._ceenDisambiguationZoom) {
          return;
        }

        // Get zoom and check threshold BEFORE loaded check
        if (!map.current) {
          return;
        }

        const currentZoom = map.current.getZoom();

        // Check if markers should be hidden at current zoom level
        if (!shouldShowMarkersAtZoom(currentZoom)) {
          const vendorMarkers = markerManager.getAllMarkersByType('vendor');
          popupManager.cleanupBeforeMarkerRecreation(vendorMarkers);
          markerManager.cleanupByType('vendor');
          popupRenderFunctions.current.clear();

          // Notify parent that no vendors are visible
          if (onVisibleVendorsChange) {
            onVisibleVendorsChange([]);
          }
          return;
        }

        if (locationState === 'pending') {
          logger.debug('Location permission pending, skipping marker setup');
          return;
        }

        // All checks passed, set up markers
        setupMarkers();
      } finally {
        (map.current as any)._settingUpMarkers = false;
      }
    };

    // Set up zoom listener and initial markers
    if (map.current.loaded()) {
      setupMarkersWhenReady();
    } else {
      map.current.once('load', () => {
        setupMarkersWhenReady();
      });
    }

    return () => {
      const vendorMarkers = markerManager.getAllMarkersByType('vendor');
      popupManager.cleanupBeforeMarkerRecreation(vendorMarkers);
      markerManager.cleanupByType('vendor');
      popupRenderFunctions.current.clear();
    };
  }, [filteredVendors, locationState]);

  // GCAL MARKERS useEffect - unified with MarkerManager; filtered to currentDate
  useEffect(() => {
    markerManager.cleanupByType('gcal');

    const currentDateStr = currentDate ? format(currentDate, 'yyyy-MM-dd') : null;
    const dateEvents = currentDateStr
      ? (gcalEvents ?? []).filter(e => e.start_datetime?.slice(0, 10) === currentDateStr)
      : [];

    if (!map.current || !dateEvents.length) return;
    const mapRef = map.current;

    const addGCalMarkers = () => {
      const showMarkers = shouldShowMarkersAtZoom(mapRef.getZoom());
      dateEvents.forEach(event => {
        if (event.lat == null || event.lng == null) return;

        const el = document.createElement('div');
        el.style.cssText = [
          'width:28px',
          'height:28px',
          'border-radius:50%',
          'background:var(--color-primary)',
          'border:2px solid white',
          'box-shadow:0 2px 6px rgba(0,0,0,0.25)',
          'align-items:center',
          'justify-content:center',
          'cursor:pointer',
          'touch-action:manipulation',
          `display:${showMarkers ? 'flex' : 'none'}`,
        ].join(';');
        el.innerHTML = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>`;
        el.title = event.title;

        const popupContainer = document.createElement('div');
        const root = createRoot(popupContainer);
        root.render(<GCalEventCard event={event} />);

        const popup = new maptilersdk.Popup({
          closeButton: false,
          closeOnClick: false,
          closeOnMove: false,
          focusAfterOpen: false,
          maxWidth: isMobile ? '280px' : '300px',
          className: 'clean-popup',
          offset: 18,
        }).setDOMContent(popupContainer);

        const locationKey = `gcal_${event.gcal_id}`;
        const marker = markerManager.createMarker(
          el,
          [event.lng, event.lat],
          root,
          locationKey,
          [],
          'gcal'
        );
        marker.setPopup(popup);
        markerManager.addToMap(locationKey, mapRef);

        popup.on('open', () => popupManager.register(popup, event.gcal_id));
        popup.on('close', () => popupManager.unregister(popup));

        const handleClick = (e: Event) => {
          e.stopPropagation();
          if (e.type === 'touchend') e.preventDefault();
          const p = marker.getPopup();
          if (!p) return;
          if (p.isOpen()) {
            p.remove();
          } else {
            popupManager.closeAllExcept(marker, markerManager.getAllMarkers());
            p.setLngLat(marker.getLngLat());
            p.addTo(mapRef);
          }
        };
        el.addEventListener('click', handleClick);
        el.addEventListener('touchend', handleClick);
        markerManager.registerEventListener(locationKey, el, 'click', handleClick);
        markerManager.registerEventListener(locationKey, el, 'touchend', handleClick);
      });
    };

    if (mapRef.loaded()) {
      addGCalMarkers();
    } else {
      mapRef.once('load', addGCalMarkers);
    }

    return () => {
      markerManager.cleanupByType('gcal');
      mapRef.off('load', addGCalMarkers);
    };
  }, [gcalEvents, currentDate, isMobile]);

  // ZOOM STYLE useEffect - mutates styles on zoom, handles show/hide threshold
  useEffect(() => {
    if (!map.current) return;

    const handleZoomStyle = () => {
      if (!map.current) return;
      const zoom = map.current.getZoom();

      if (!shouldShowMarkersAtZoom(zoom)) {
        // Hide GCal markers at low zoom alongside vendor markers
        markerManager.getAllMarkersByType('gcal').forEach(m => {
          const el = m.getElement();
          if (el) el.style.display = 'none';
        });
        if (markerManager.getAllMarkersByType('vendor').length > 0) {
          const vendorMarkers = markerManager.getAllMarkersByType('vendor');
          popupManager.cleanupBeforeMarkerRecreation(vendorMarkers);
          markerManager.cleanupByType('vendor');
          popupRenderFunctions.current.clear();
          if (onVisibleVendorsChange) onVisibleVendorsChange([]);
        }
        return;
      }

      // Restore GCal marker visibility when zooming back in above threshold
      markerManager.getAllMarkersByType('gcal').forEach(m => {
        const el = m.getElement();
        if (el) el.style.display = 'flex';
      });

      if (markerManager.getAllMarkersByType('vendor').length === 0 && locationState !== 'pending') {
        setupMarkers();
        return;
      }

      markerManager.applyZoomStyles(
        getMarkerStyles,
        vendorIds => {
          const group = locationGroupsRef.current.find(g =>
            g.vendors.some(v => vendorIds.includes(v.id))
          );
          if (!group) return false;
          const rep = group.vendors[0];
          const requirements = rep.metadata?.requirements;
          return (
            Array.isArray(requirements) &&
            requirements.length === 1 &&
            requirements[0] === 'just show up'
          );
        },
        popupManager.getOpenMarkerId(),
        zoom
      );
    };

    map.current.on('zoomend', handleZoomStyle);

    return () => {
      map.current?.off('zoomend', handleZoomStyle);
    };
  }, [locationState, onVisibleVendorsChange]);

  // SELECTION UPDATE useEffect - updates styles and popup content when selection changes
  useEffect(() => {
    // Update popup contents for open popups only
    popupRenderFunctions.current.forEach((renderFunction, locationKey) => {
      // Find marker at this location
      const vendorMarker = markerManager.getAllMarkers().find(marker => {
        const markerLocationKey = (marker as any)._ceenLocationKey;
        return markerLocationKey === locationKey;
      });

      if (vendorMarker?.getPopup()?.isOpen()) {
        renderFunction(selectedDisplayIds || []);
      }
    });

    // Update marker styles - FIX: Check if marker still exists before updating
    markerManager.getAllMarkers().forEach(marker => {
      const coords = marker.getLngLat();

      // Find vendor group for this marker location
      const locationGroups = locationGroupsRef.current;
      const markerGroup = locationGroups.find(
        group =>
          Math.abs(group.coordinates[0] - coords.lng) < 0.000001 &&
          Math.abs(group.coordinates[1] - coords.lat) < 0.000001
      );

      if (markerGroup && map.current) {
        const representativeVendor = currentDate
          ? getEventForCurrentDate(markerGroup.vendors, currentDate) ||
            getLatestEvent(markerGroup.vendors)
          : getLatestEvent(markerGroup.vendors);
        const isPopupOpen = popupManager.getOpenMarkerId() === representativeVendor.id;
        const markerElement = marker.getElement();

        if (markerElement) {
          // Update marker visual style - apply to the visual child, not container
          const eventCount = markerGroup.vendors.length;
          const representativeIsFree = isFreeEvent(representativeVendor);
          const styles = getMarkerStyles(isPopupOpen, representativeIsFree);

          // Find the visual marker child element
          const visualMarker = markerElement.querySelector('.marker-visual') as HTMLElement;
          if (visualMarker) {
            Object.assign(visualMarker.style, styles);

            // Update count display on the visual marker
            if (eventCount > 1) {
              visualMarker.textContent = eventCount.toString();
            } else {
              visualMarker.textContent = '';
            }
          }
        }
      }
    });
  }, [selectedVendorId, selectedDisplayIds]);

  // Keep activeListItemsRef current so popup render functions always read the latest value
  useEffect(() => {
    activeListItemsRef.current = activeListItems;
  }, [activeListItems]);

  // PIN STATE SYNC useEffect - re-renders any open popup when activeListItems changes
  // Without this, popup render functions use a stale closure from marker-creation time
  useEffect(() => {
    popupRenderFunctions.current.forEach((renderFn, locationKey) => {
      const marker = markerManager
        .getAllMarkers()
        .find(m => (m as any)._ceenLocationKey === locationKey);
      if (marker?.getPopup()?.isOpen()) {
        renderFn(selectedDisplayIds || []);
      }
    });
  }, [activeListItems]);

  // DIMMING useEffect - mutates marker opacity when active filter changes, never recreates markers
  useEffect(() => {
    markerManager.applyDimming(activeVendorIds ?? null);
  }, [activeVendorIds]);

  // Enhanced map click handler to close popups when clicking elsewhere
  useEffect(() => {
    if (!map.current) return;

    let touchStartTime = 0;
    let touchStartPosition = { x: 0, y: 0 };
    const TOUCH_THRESHOLD = 10; // pixels
    const TAP_TIME_THRESHOLD = 300; // milliseconds

    // Enhanced handler to differentiate taps vs pans
    const handleMapInteraction = (e: any) => {
      logger.debug(`Map interaction detected - event type: ${e.type}`);

      // For touch events, check if it's a tap vs pan gesture
      if (e.type === 'touchstart') {
        touchStartTime = Date.now();
        touchStartPosition = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        return; // Don't close on touchstart
      }

      if (e.type === 'touchend') {
        const touchEndTime = Date.now();
        const touchDuration = touchEndTime - touchStartTime;

        // If it was a long touch or moved too much, don't treat as tap
        if (touchDuration > TAP_TIME_THRESHOLD) {
          logger.debug(`Long touch detected (${touchDuration}ms), not closing popups`);
          return;
        }

        if (e.changedTouches && e.changedTouches[0]) {
          const touchEndPosition = {
            x: e.changedTouches[0].clientX,
            y: e.changedTouches[0].clientY,
          };
          const distance = Math.sqrt(
            Math.pow(touchEndPosition.x - touchStartPosition.x, 2) +
              Math.pow(touchEndPosition.y - touchStartPosition.y, 2)
          );

          if (distance > TOUCH_THRESHOLD) {
            logger.debug(`Touch moved too much (${distance}px), not closing popups`);
            return;
          }
        }

        logger.debug(`Quick tap detected, checking if should close popups`);
      }

      // Safety check: ensure e.target exists and is a proper DOM element
      if (!e.target || typeof e.target.closest !== 'function') {
        logger.debug(`Closing popups - target is not a DOM element (likely canvas)`);
        let closedPopups = 0;
        markerManager.getAllMarkers().forEach(m => {
          const popup = m.getPopup();
          // Use openPopupsOnMap instead of isOpen()
          if (popup && popupManager.isTracked(popup)) {
            popup.remove();
            popupManager.unregister(popup);
            closedPopups++;
          }
        });
        logger.debug(`Closed ${closedPopups} popups`);
        return;
      }

      // Check if the interaction target is the map canvas or container
      const isMapCanvas =
        e.target &&
        (e.target.tagName === 'CANVAS' ||
          e.target.classList?.contains('maplibregl-canvas') ||
          e.target.classList?.contains('mapboxgl-canvas') ||
          e.target.classList?.contains('map'));

      const mapContainerEl = mapContainer.current;
      const isMapArea = mapContainerEl && mapContainerEl.contains(e.target);

      // Safely check if the target is NOT a popup or marker
      let isNotPopupOrMarker = true;
      try {
        isNotPopupOrMarker =
          !e.target.closest('.maplibregl-popup') &&
          !e.target.closest('.marker') &&
          !e.target.closest('.maptiler-popup');
      } catch (_error) {
        logger.debug(`closest() failed, assuming not popup/marker`);
        isNotPopupOrMarker = true;
      }

      if ((isMapCanvas || isMapArea) && isNotPopupOrMarker) {
        logger.debug(`Closing popups - interaction was on map area`);

        let closedPopups = 0;
        markerManager.getAllMarkers().forEach(m => {
          const popup = m.getPopup();

          if (popup && popupManager.isTracked(popup)) {
            try {
              popupManager.close(popup);
              closedPopups++;
            } catch (e) {
              logger.warn('Error closing popup:', e);
            }
          }
        });

        logger.debug(`Closed ${closedPopups} popups`);

        if (onMapTouched && isMobile) {
          onMapTouched();
        }
      } else {
        logger.debug(`Not closing popups - interaction was not on map area`);
      }
    };

    // Add click handler
    map.current.on('click', handleMapInteraction);

    if (touchEnabled.current) {
      const mapContainerEl = mapContainer.current;
      if (mapContainerEl) {
        // Use passive: true to avoid blocking the browser's touch handling
        const options: AddEventListenerOptions = { passive: true };
        mapContainerEl.addEventListener('touchstart', handleMapInteraction, options);
        mapContainerEl.addEventListener('touchend', handleMapInteraction, options);
      }
    }

    return () => {
      if (map.current) {
        map.current.off('click', handleMapInteraction);
      }

      if (touchEnabled.current) {
        const mapContainerEl = mapContainer.current;
        if (mapContainerEl) {
          const options: AddEventListenerOptions = { passive: true };
          mapContainerEl.removeEventListener('touchstart', handleMapInteraction, options);
          mapContainerEl.removeEventListener('touchend', handleMapInteraction, options);
        }
      }
    };
  }, [touchEnabled.current, isMobile, onMapTouched]);

  useEffect(() => {
    const handleLocationChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ location: string }>;
      const location = customEvent.detail.location;
      logger.debug('Map: Received locationChange event for:', location);

      const LOCATION_COORDINATES = {
        oakland: { lat: 37.8044, lng: -122.2711, zoom: 12 },
        park_city: { lat: 40.6461, lng: -111.498, zoom: 12 },
        nyc: { lat: 40.674, lng: -73.9569, zoom: 12 },
        goa: { lat: 15.2993, lng: 74.124, zoom: 12 },
        japan: { lat: 35.0007, lng: 135.7535, zoom: 12 },
      };

      const coords = LOCATION_COORDINATES[location as keyof typeof LOCATION_COORDINATES];
      if (coords && map.current) {
        logger.debug('Map: Moving to:', coords);
        map.current.flyTo({
          center: [coords.lng, coords.lat],
          zoom: coords.zoom,
          speed: 0.5,
          curve: 1.42,
          padding: { top: 50, bottom: 50, left: 50, right: 50 },
        });

        // Listen for moveend to signal flyTo completion
        const handleInitialMoveEnd = () => {
          logger.debug('Map: Initial flyTo completed, dispatching event');
          window.dispatchEvent(new CustomEvent('initialMapMoveComplete'));
          map.current?.off('moveend', handleInitialMoveEnd);
        };
        map.current.once('moveend', handleInitialMoveEnd);
      }
    };

    window.addEventListener('locationChange', handleLocationChange);
    return () => window.removeEventListener('locationChange', handleLocationChange);
  }, []);

  useEffect(() => {
    if (!mapContainer.current || !onMapLongPress) return;
    const handlers = createLongPressHandlers(
      (x, y) => {
        if (!map.current) return;
        // Cancel if pointer is on a marker
        const el = document.elementFromPoint(x, y);
        if (el?.closest('.marker-touch-area')) return;
        setLpIndicator(prev => (prev ? { ...prev, fired: true } : null));
        const lngLat = map.current.unproject([x, y]);
        onMapLongPress({ lng: lngLat.lng, lat: lngLat.lat });
      },
      900, // total press duration (ms)
      10,
      {
        startDelay: 400, // visual only starts after 400ms — casual taps stay silent
        onStart: (x, y) => setLpIndicator({ active: true, x, y, fired: false }),
        onCancel: () => setLpIndicator(prev => (prev ? { ...prev, active: false } : null)),
      }
    );
    const container = mapContainer.current;

    const handlePointerDown = (e: PointerEvent) => {
      container.classList.add('map-longpress-active');
      handlers.onPointerDown(e);
    };
    const handlePointerUp = (e: PointerEvent) => {
      container.classList.remove('map-longpress-active');
      handlers.onPointerUp(e);
    };

    container.addEventListener('pointerdown', handlePointerDown);
    container.addEventListener('pointermove', handlers.onPointerMove);
    container.addEventListener('pointerup', handlePointerUp);
    container.addEventListener('pointercancel', handlePointerUp);
    return () => {
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlers.onPointerMove);
      container.removeEventListener('pointerup', handlePointerUp);
      container.removeEventListener('pointercancel', handlePointerUp);
      container.classList.remove('map-longpress-active');
    };
  }, [onMapLongPress]);

  return (
    <>
      <div
        className="map-wrap"
        style={{
          zIndex: 5, // Higher z-index to ensure it's above background but below UI elements
          touchAction: 'none',
          WebkitTapHighlightColor: 'transparent', // Prevent tap highlight on iOS
        }}
      >
        <div
          ref={mapContainer}
          className="map"
          style={{
            zIndex: 5,
            touchAction: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none',
          }}
        />
        {lpIndicator && (
          <LongPressIndicator
            x={lpIndicator.x}
            y={lpIndicator.y}
            active={lpIndicator.active}
            fired={lpIndicator.fired}
            duration={500} // 900ms total − 400ms startDelay
            onBurstComplete={() => setLpIndicator(null)}
          />
        )}
        {toastMessage && (
          <div
            className={`location-toast ${toastMessage.type} ${toastMessage.isDismissing ? 'dismissing' : ''}`}
            style={{
              position: 'absolute',
              top: isMobile ? '160px' : '80px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              padding: '12px 24px',
              borderRadius: '8px',
              backgroundColor: 'transparent',
              color: 'var(--color-accent)',
              fontSize: isMobile ? '14px' : '18px',
              fontWeight: '600',
              textAlign: 'center',
              boxShadow: 'none',
              textShadow: '0 1px 3px rgba(190, 190, 190, 0.8)',
              pointerEvents: 'none',
            }}
          >
            {toastMessage.text}
          </div>
        )}
      </div>
      {previewScreenPos &&
        createPortal(
          <>
            {/* Coordinate anchor — always marks the exact geo position */}
            <div
              style={{
                position: 'fixed',
                left: previewScreenPos.x,
                top: previewScreenPos.y,
                transform: 'translate(-50%, -50%)',
                zIndex: 10001,
                pointerEvents: 'none',
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: 'var(--color-yellow)',
                boxShadow: '0 0 0 2.5px rgba(0,0,0,0.7), 0 0 0 5px rgba(244,180,26,0.3)',
              }}
            />
            {/* Outer shell — owns position + lift transform only, no animation */}
            <div
              style={{
                position: 'fixed',
                left: previewScreenPos.x,
                top: previewScreenPos.y,
                transform: isPreviewDragging
                  ? 'translate(-50%, calc(-100% - 35px))'
                  : 'translate(-50%, -100%)',
                transition: isPreviewDragging ? 'none' : 'transform 0.2s ease',
                zIndex: 10001,
                pointerEvents: previewMarkerPopping ? 'none' : 'auto',
                cursor: isPreviewDragging ? 'grabbing' : 'grab',
              }}
              onPointerDown={handlePreviewPointerDown}
            >
              {/* Inner — owns drop/pop animations; transform-origin bottom center */}
              <div
                className={['preview-marker', previewMarkerPopping ? 'preview-marker-pop' : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                {isPreviewDragging && (
                  <>
                    <div className="preview-drag-arrow preview-drag-arrow-n" />
                    <div className="preview-drag-arrow preview-drag-arrow-s" />
                    <div className="preview-drag-arrow preview-drag-arrow-w" />
                    <div className="preview-drag-arrow preview-drag-arrow-e" />
                  </>
                )}
                <PreviewPin />
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

export default React.memo(MapComponent, (prevProps, nextProps) => {
  const vendorsUnchanged = prevProps.filteredVendors.length === nextProps.filteredVendors.length;
  const selectionUnchanged = prevProps.selectedVendorId === nextProps.selectedVendorId;
  const displayIdsUnchanged =
    prevProps.selectedDisplayIds?.length === nextProps.selectedDisplayIds?.length;
  const dateUnchanged = prevProps.currentDate?.getTime() === nextProps.currentDate?.getTime();

  // Catch null <-> Set transitions and size changes
  const prevActive = prevProps.activeVendorIds ?? null;
  const nextActive = nextProps.activeVendorIds ?? null;
  const dimmingUnchanged =
    (prevActive === null && nextActive === null) ||
    (prevActive !== null && nextActive !== null && prevActive.size === nextActive.size);

  // Must re-render when preview coords or popping state change
  const previewUnchanged = prevProps.previewMarkerCoords === nextProps.previewMarkerCoords;
  const poppingUnchanged = prevProps.previewMarkerPopping === nextProps.previewMarkerPopping;
  const gcalEventsUnchanged = prevProps.gcalEvents === nextProps.gcalEvents;

  return (
    vendorsUnchanged &&
    selectionUnchanged &&
    displayIdsUnchanged &&
    dateUnchanged &&
    dimmingUnchanged &&
    previewUnchanged &&
    poppingUnchanged &&
    gcalEventsUnchanged
  );
});
